import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { IconFilter } from '../../../../shared/icon/icon'
import { UTC, addDays, agoText, dateText, dateTimeText, dayOf, startOfDay, timeText } from '../../../../shared/date/local-time'
import { RANGES } from '../../../../shared/audit/filters-model'
import { formatCount, plural } from '../../../../shared/sso-workspace/format'

// The words of the AI tools telemetry staff page (spec 2026-10-20 §9;
// contract §4.6, §8). The server is the authority; these only shape what
// the page offers and say what it answered.

export const SOURCE_WORDS = { 'caffeine-tools': 'Caffeine tools', 'caffeine-mcp': 'Caffeine MCP' }
export const sourceText = source => SOURCE_WORDS[source] || source || ''

// A mark's status (ai_tools_processing.status), and Unprocessed: the mark
// dialog's way to remove a consumer's mark (contract R20), so its next
// export picks the record up again.
export const MARK_STATUSES = [
  { value: 'processed', label: 'Processed', tone: 'ok' },
  { value: 'skipped', label: 'Skipped', tone: null },
  { value: 'needs_review', label: 'Needs review', tone: 'warn' },
  { value: 'unprocessed', label: 'Clear the mark', tone: null }
]
const STATUS = Object.fromEntries(MARK_STATUSES.map(s => [s.value, s]))
export const statusText = status => (STATUS[status] ? STATUS[status].label : String(status || ''))

// The lists' filters. A status with a consumer is that consumer's; without
// one it looks across every consumer ("unprocessed": no consumer has marked
// the row, or (a session) none as new as its last record).
export const STATUS_FILTERS = [
  { value: '', label: 'Any status' },
  { value: 'processed', label: 'Processed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'unprocessed', label: 'Needs processing' }
]
// The lists open on what is still to be processed (owner 2026-10-29: "showing
// the new unprocessed things by default"); Any status is one pick away.
export const DEFAULT_STATUS = 'unprocessed'
export const SOURCE_FILTERS = [
  { value: '', label: 'Any source' },
  { value: 'caffeine-tools', label: 'Caffeine tools' },
  { value: 'caffeine-mcp', label: 'Caffeine MCP' }
]
export const EVENT_KIND_FILTERS = [
  { value: '', label: 'Every kind' },
  { value: 'session', label: 'Session headers' },
  { value: 'call', label: 'Calls' },
  { value: 'friction', label: 'Friction' },
  { value: 'postmortem_submitted', label: 'Postmortems submitted' }
]

// The query a list sends (the pager drops the empty values). login: one
// person's rows (the person column's filter).
export const listFilters = ({ organization = '', source = '', consumer = '', status = '', login = '', client = '' } = {}) => ({
  organization_id: organization,
  source,
  consumer,
  status,
  login_id: login,
  client_app: client
})

// What each kind of row is called (a session's records are events to the
// server).
export const KIND_WORDS = {
  session: { noun: 'session', items: 'sessions' },
  event: { noun: 'record', items: 'records' },
  postmortem: { noun: 'postmortem', items: 'postmortems' },
  finding: { noun: 'finding', items: 'findings' }
}
export const kindNoun = kind => (KIND_WORDS[kind] || KIND_WORDS.event).noun
export const kindItems = kind => (KIND_WORDS[kind] || KIND_WORDS.event).items

// One action takes at most this many rows (contract: a selection, a filter);
// more answers 413 ai_tools.too_many_matches and nothing is written.
export const MATCH_LIMIT = 10000
export const isTooMany = err => Boolean(err) && (err.status === 413 || Boolean(err.data && err.data.code === 'ai_tools.too_many_matches'))
export const tooManyText = kind =>
  `More than ${formatCount(MATCH_LIMIT)} ${kindItems(kind)} match, and one action takes at most ${formatCount(MATCH_LIMIT)}, so nothing was changed. Narrow the filters or the search, or select fewer.`

// "Move 338 sessions to the recycle bin?" and what goes with them: a
// session's records, postmortems and findings, a postmortem's findings
// (the server stamps only the rows chosen; their children follow them).
const GOES_WITH = { session: 'records, postmortems and findings', postmortem: 'findings' }
export const deleteQuestion = (kind, count) =>
  `Move ${count === null || count === undefined ? `these ${kindItems(kind)}` : plural(count, kindNoun(kind))} to the recycle bin?`
export const deleteBody = (kind, count) => {
  const one = count === 1
  const children = GOES_WITH[kind] ? `${one ? 'Its' : 'Their'} ${GOES_WITH[kind]} go with ${one ? 'it' : 'them'}. ` : ''
  return `${children}You can restore ${one ? 'it' : 'them'} from the recycle bin.`
}

// The person a row came from (contract: person { loginId, name, email } |
// null; null when the row is unlinked or the login is gone). Their staff
// page is the login's.
export const personText = person => (person ? (person.name || person.email || `Login #${person.loginId}`) : '')
export const personPage = person => (person && person.loginId ? `/admin/login/list/edit/${encodeURIComponent(person.loginId)}` : null)

// The address with one parameter set (and the grid's scroll reset), kept
// otherwise: the person column's "only this person" link.
const withParam = (search, key, value) => {
  const next = new URLSearchParams(search)
  next.set(key, String(value))
  return `?${next.toString()}`
}

// A row's person: name over email, the name linking to their staff page,
// and a filter button that narrows the list to them (the `login` filter in
// the address: a replace, like every filter). Unlinked is the Unlinked pill.
// org: the row's organization on the second line in place of the email (the
// email is then the name's hover), so a list carries the person and their
// organization in one column and fits a laptop's window (owner 2026-10-29);
// client: the client app they used there instead (a finding, whose row names
// no organization).
const PersonInCell = ({ row, filter, org, client }) => {
  const location = useLocation()
  const person = row.person
  if (!person) return isUnlinked(row) ? <Pill title={UNLINKED_NOTE}>Unlinked</Pill> : <span className="sw-hint">Not known</span>
  const name = personText(person)
  const page = personPage(person)
  const sub = org ? row.linkText : client ? row.clientApp || 'Client app not named' : person.name && person.email ? person.email : ''
  return (
    <span className="ai-person-cell">
      <span className="ak-name-cell min-w-0">
        {page ? <Link className="text-truncate" to={page} title={person.email || undefined} onClick={e => e.stopPropagation()}>{name}</Link> : <span className="text-truncate" title={person.email || undefined}>{name}</span>}
        {/* the space keeps the two lines apart in the cell's text (read
            aloud, and measured: an email run into "Cursor" read as one long
            address, which the table never cuts) */}
        {sub && ' '}
        {sub && <span className="sw-cell-sub text-truncate" title={sub}>{sub}</span>}
      </span>
      {filter &&
        <Link replace className="btn btn-sm ai-person-only" to={{ pathname: location.pathname, search: withParam(location.search, 'login', person.loginId) }}
          aria-label={`Only ${name}`} title={`Show only ${name}'s rows`} onClick={e => e.stopPropagation()}>
          <IconFilter />
        </Link>}
    </span>
  )
}
export const personCell = cell => (cell.data ? <PersonInCell row={cell.data} filter /> : '')
// The same, with the organization under the person.
export const personOrgCell = cell => (cell.data ? <PersonInCell row={cell.data} filter org /> : '')
// The same, with the client app under the person.
export const personClientCell = cell => (cell.data ? <PersonInCell row={cell.data} filter client /> : '')
// The organization under the person, without "only this person" (a list with
// no person filter: the recycle bin).
export const personNameOrgCell = cell => (cell.data ? <PersonInCell row={cell.data} org /> : '')

// Who moved a row to the recycle bin (deletedBy: { loginId, name, email } | null).
export const deletedByText = by => (by ? personText(by) : 'Someone no longer here')

// "Received from" / "to": two local dates. The server wants instants: from =
// the start of the from-day, to = the start of the day AFTER the to-day (so
// the to-day is in), both the viewer's local midnight. A to-day before the
// from-day is a problem, and nothing is asked then.
const DAY_RULE = /^\d{4}-\d{2}-\d{2}$/
const midnight = day => new Date(startOfDay(day)).toISOString()
export const receivedRange = (fromDay = '', toDay = '') => {
  const from = DAY_RULE.test(fromDay) ? fromDay : ''
  const to = DAY_RULE.test(toDay) ? toDay : ''
  if (from && to && to < from) return { from: '', to: '', problem: 'The "to" date is before the "from" date.' }
  return { from: from ? midnight(from) : '', to: to ? midnight(addDays(to, 1)) : '', problem: null }
}

// "Received" as the house time range picks it (shared/date/time-range-filter.js:
// { range, from, to }) -> the instants the server takes, its problem, and the
// days that name a download's file. A preset reaches back from now with an
// open end, so a row that arrives later is in the list when it reloads; a
// custom range is its days (receivedRange); Any time ('all') is no bound.
export const RECEIVED_ANY = 'all'
export const receivedWindow = ({ range = RECEIVED_ANY, from = '', to = '' } = {}, now = Date.now()) => {
  if (range === 'custom') {
    return { ...receivedRange(from, to), fromDay: DAY_RULE.test(from) ? from : '', toDay: DAY_RULE.test(to) ? to : '' }
  }
  const preset = RANGES.find(r => r.value === range && r.ms)
  if (!preset) return { from: '', to: '', problem: null, fromDay: '', toDay: '' }
  const start = now - preset.ms
  return { from: new Date(start).toISOString(), to: '', problem: null, fromDay: dayOf(start), toDay: '' }
}
export const isRange = value => value === RECEIVED_ANY || RANGES.some(r => r.value === value)
export const isDay = value => value === '' || DAY_RULE.test(value)

// What a consumer is, said where it is a filter and where it is a field.
export const CONSUMER_HELP = 'Who did the processing: the name of a script or a person, such as triage. Each name keeps its own marks, so one never hides rows from another.'

// A consumer names whatever processes the telemetry (a script, a person):
// the server's rule (ai_tools_processing.consumer), and its note's limit.
export const CONSUMER_RULE = /^[a-z0-9][a-z0-9._-]{0,63}$/
export const NOTE_MAX = 1000
export const consumerProblem = consumer => (CONSUMER_RULE.test(String(consumer || ''))
  ? null
  : 'A consumer is 1 to 64 lowercase letters, digits, dots, dashes and underscores, starting with a letter or digit.')
export const noteProblem = note => ([...String(note || '')].length > NOTE_MAX ? `A note can be at most ${formatCount(NOTE_MAX)} characters.` : null)

const marksOf = marks => (Array.isArray(marks) ? marks : [])

export const marksText = marks => (marksOf(marks).length
  ? marksOf(marks).map(mark => `${mark.consumer}: ${statusText(mark.status)}`).join(', ')
  : 'Not marked')

const markTitle = mark => [
  mark.markedAt ? `Marked ${dateTimeText(mark.markedAt)}` : '',
  mark.markedByKind === 'api_key' ? 'by an API key' : mark.markedByKind === 'user' ? 'by EducationXR staff' : '',
  mark.note || ''
].filter(Boolean).join(' · ')

// A row's marks: one pill per consumer, its status in the workspace's tones.
export const MarkPills = ({ marks }) => {
  const list = marksOf(marks)
  if (!list.length) return <span className="sw-hint">Not marked</span>
  return (
    <span className="d-inline-flex flex-wrap gap-1">
      {list.map(mark =>
        <Pill key={mark.consumer} tone={STATUS[mark.status] ? STATUS[mark.status].tone : null} title={markTitle(mark) || undefined}>
          {`${mark.consumer}: ${statusText(mark.status)}`}
        </Pill>)}
    </span>
  )
}

// Who a row is linked to. A stop, an account deletion or an organization's
// deletion unlinks rows (spec §7: user, organization and login nulled; a
// session says `unlinked`). Such a row is "Unlinked", never an id that isn't
// there.
export const UNLINKED_NOTE = 'The person stopped sharing, or their account or organization was deleted: the records stay, linked to no one.'
export const isUnlinked = row => !row || row.unlinked === true || row.organizationId === null || row.organizationId === undefined
// An unlinked row's server time is cut to its UTC day (the unlink does that, by
// design): it is shown as that day, in UTC, never as a local time of day.
export const unlinkedDayText = value => `${dateText(value, { tz: UTC })} (UTC)`
export const UNLINKED_DAY_NOTE = 'The server keeps only the UTC day of an unlinked record, so there is no time of day.'
export const linkText = row => {
  if (isUnlinked(row)) return 'Unlinked'
  return row.organizationName ? `${row.organizationName} (#${row.organizationId})` : `Organization #${row.organizationId}`
}

// Grid cells shared by the lists (module-level, so AG Grid keeps its cells).
export const linkCell = cell => {
  const row = cell.data
  if (!row) return ''
  return isUnlinked(row) ? <Pill title={UNLINKED_NOTE}>Unlinked</Pill> : <span className="text-truncate">{row.linkText}</span>
}
export const marksCell = cell => (cell.data ? <MarkPills marks={cell.data.marks} /> : '')

// A postmortem is always held to one limit (a session's records have their kind's).
export const POSTMORTEM_SOFT_LIMIT_NOTE = 'Larger than the 64 KB soft limit for a postmortem: stored whole, and counted in the daily summary.'

// A stored record, header or body as received, for a scrolling block; never
// a grid cell or a row's text field (a header can be large).
export const jsonText = value => {
  if (value === undefined || value === null || value === '') return ''
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
}

const versions = s => [
  s.toolsVersion && `tools ${s.toolsVersion}`,
  s.mcpVersion && `MCP ${s.mcpVersion}`,
  s.packageVersion && `package ${s.packageVersion}`,
  s.caffeineVersion && `caffeine ${s.caffeineVersion}`,
  s.sdkVersion && `SDK ${s.sdkVersion}`,
  s.unityVersion && `Unity ${s.unityVersion}`
].filter(Boolean).join(' · ')

// A session's counts stop at a ceiling (10,000 events in the list, 100,000 in
// its own page); countsCapped says the row hit it, and then every count is
// "at least" (the friction count is of the events counted).
const countsText = s => {
  const atLeast = s.countsCapped === true ? '≥ ' : ''
  return [plural(s.eventCount || 0, 'event'), plural(s.frictionCount || 0, 'friction record'), plural(s.postmortemCount || 0, 'postmortem')]
    .map(text => atLeast + text).join(' · ')
}

// The list's Records cell, short enough to sit beside the other columns at
// 1280 px: the events always, friction and postmortems only when there are
// some ("8 events · 1 postmortem"); countsText keeps every count.
const recordsText = s => {
  const atLeast = s.countsCapped === true ? '≥ ' : ''
  return [plural(s.eventCount || 0, 'event'), s.frictionCount ? plural(s.frictionCount, 'friction', 'friction') : '', s.postmortemCount ? plural(s.postmortemCount, 'postmortem') : '']
    .filter(Boolean).map(text => atLeast + text).join(' · ')
}

// When a row was received, as the lists show it: how long ago ("3 hours ago",
// "Oct 22"), short enough for a laptop's window, with the exact time on
// hover (receivedText). An unlinked row keeps its UTC day: it has no time.
export const receivedAgo = (row, unlinked = isUnlinked(row), now = Date.now()) => (!row.receivedAt ? '' : unlinked ? unlinkedDayText(row.receivedAt) : agoText(row.receivedAt, { now }))

// When a session ran, under its Received time: "Ran 10:52 – 10:55 AM" within
// one of the viewer's days, else both ends with their days.
export const runText = (first, last) => {
  if (!first) return ''
  if (!last || Date.parse(last) - Date.parse(first) < 60e3) return `Ran at ${timeText(first, { zone: false })}`
  return dayOf(first) === dayOf(last)
    ? `Ran ${timeText(first, { zone: false })} – ${timeText(last, { zone: false })}`
    : `Ran ${dateText(first, { year: false })} – ${dateText(last, { year: false })}`
}

// A binned row (the recycle bin's, a binned record's page): when, by whom.
const binnedText = r => (r.deletedAt ? { deletedText: dateTimeText(r.deletedAt), deletedByText: deletedByText(r.deletedBy) } : {})

// The rows: each column's field holds the words it shows (the grid exports
// the field's value). The server's own fields stay on the row.
export const sessionRow = s => ({
  ...s,
  sessionText: s.session || '',
  sourceText: sourceText(s.source),
  channelText: s.channel || '',
  linkText: linkText(s),
  versionText: s.toolsVersion || s.mcpVersion || '',
  versionsText: versions(s),
  countsText: countsText(s),
  recordsText: recordsText(s),
  runText: runText(s.firstSeenTs, s.lastSeenTs),
  clientAppText: s.clientApp || '',
  startedText: s.firstSeenTs ? dateTimeText(s.firstSeenTs) : '',
  lastSeenText: s.lastSeenTs ? dateTimeText(s.lastSeenTs) : '',
  // The server cuts an unlinked row's time to the UTC day: only that UTC date is true, so it is
  // shown in UTC whatever the viewer's zone.
  receivedText: s.receivedAt ? (isUnlinked(s) ? unlinkedDayText(s.receivedAt) : dateTimeText(s.receivedAt)) : '',
  personText: personText(s.person),
  marksText: marksText(s.marks),
  ...binnedText(s)
})

export const postmortemRow = p => ({
  ...p,
  titleText: p.title || 'Untitled postmortem',
  sessionText: p.session || '',
  sourceText: sourceText(p.source),
  linkText: linkText(p),
  whenText: p.ts ? dateTimeText(p.ts) : '',
  findingsText: formatCount(p.findingCount || 0),
  flagsText: p.overSoftLimit ? 'Over the soft limit' : '',
  personText: personText(p.person),
  marksText: marksText(p.marks),
  ...binnedText(p)
})

export const SEVERITY_FILTERS = [
  { value: '', label: 'Any severity' },
  { value: 'critical', label: 'critical' },
  { value: 'high', label: 'high' },
  { value: 'medium', label: 'medium' },
  { value: 'low', label: 'low' }
]

export const SEVERITY_TONES = { critical: 'bad', high: 'bad', medium: 'warn', low: 'info' }
export const severityTone = severity => SEVERITY_TONES[String(severity || '').toLowerCase()] || null

export const findingRow = f => ({
  ...f,
  titleText: f.title || 'Untitled finding',
  severityText: f.severity || '',
  categoryText: f.category || '',
  toolText: [f.tool, f.action].filter(Boolean).join(' · '),
  whatText: f.what || '',
  whenText: f.ts ? dateTimeText(f.ts) : '',
  receivedText: f.receivedAt ? (f.unlinked === true ? unlinkedDayText(f.receivedAt) : dateTimeText(f.receivedAt)) : '',
  personText: personText(f.person),
  marksText: marksText(f.marks),
  ...binnedText(f)
})

export const eventRow = e => ({
  ...e,
  whenText: e.ts ? dateTimeText(e.ts, { seconds: true }) : '',
  // the records grid's When: the time, with its day under it
  whenTime: e.ts ? timeText(e.ts, { seconds: true, zone: false }) : '',
  whenDay: e.ts ? dateText(e.ts, { year: false }) : '',
  kindText: e.kind || '',
  toolText: [e.tool, e.action].filter(Boolean).join(' · '),
  outcomeText: e.outcome || '',
  msText: typeof e.ms === 'number' ? `${formatCount(e.ms)} ms` : '',
  resultText: [e.outcome, typeof e.ms === 'number' ? `${formatCount(e.ms)} ms` : ''].filter(Boolean).join(' · '),
  errorText: e.error || '',
  flagsText: [e.overSoftLimit ? 'Over the soft limit' : '', e.unknownKind ? 'Unknown kind' : ''].filter(Boolean).join(', '),
  marksText: marksText(e.marks),
  ...binnedText(e)
})

// The filter a list holds, as PUT /processing's `filter`, the recycle bin's
// selections and a downloaded file's header take it (camelCase, only what
// is set): kind 'session', 'postmortem', 'finding' or 'event'. A consumer
// goes only with a status: alone it filters nothing (it only says whose
// marks a list shows), and the server refuses it there. q is the grid's
// search, login_id the person; a session's records name their session
// (session_id) and their own kind (the records list's `kind`, eventKind).
const isSet = value => value !== '' && value !== undefined && value !== null
export const matchFilter = (kind, filters = {}) => {
  const filter = { kind }
  if (kind === 'event') {
    if (isSet(filters.session_id)) filter.sessionId = Number(filters.session_id)
    if (filters.kind) filter.eventKind = filters.kind
    if (filters.q) filter.q = filters.q
    return filter
  }
  if (isSet(filters.organization_id)) filter.organizationId = Number(filters.organization_id)
  ;['source', 'consumer', 'status', 'from', 'to', 'severity', 'category', 'tool', 'q'].forEach(key => { if (filters[key]) filter[key] = filters[key] })
  if (isSet(filters.login_id)) filter.loginId = Number(filters.login_id)
  if (filters.client_app) filter.clientApp = filters.client_app
  if (!filter.status) delete filter.consumer
  return filter
}

// The same filter as the lists' query (snake_case) for the export.
export const exportQuery = filter => {
  const query = {}
  if (filter.organizationId !== undefined) query.organization_id = filter.organizationId
  ;['source', 'consumer', 'status', 'from', 'to', 'q'].forEach(key => { if (filter[key]) query[key] = filter[key] })
  if (filter.loginId !== undefined) query.login_id = filter.loginId
  if (filter.clientApp) query.client_app = filter.clientApp
  return query
}

// Whether two filters match the same rows.
export const sameFilter = (a, b) => {
  const keys = filter => Object.keys(filter || {}).filter(key => filter[key] !== undefined && filter[key] !== '').sort()
  const ka = keys(a)
  const kb = keys(b)
  return ka.length === kb.length && ka.every((key, i) => key === kb[i] && String(a[key]) === String(b[key]))
}

// A filter in words, for a dialog: "organization #77 · source Caffeine tools ·
// status Needs processing · received from Oct 1, 2026 to before Oct 8, 2026".
export const filterWords = filter => {
  const f = filter || {}
  const STATUS_WORDS = { processed: 'Processed', skipped: 'Skipped', needs_review: 'Needs review', unprocessed: 'Needs processing' }
  const parts = [
    f.organizationId !== undefined && `organization #${f.organizationId}`,
    f.source && `source ${sourceText(f.source)}`,
    f.consumer && `processed by ${f.consumer}`,
    f.status && `status ${STATUS_WORDS[f.status] || f.status}`,
    f.from && `received from ${dateTimeText(f.from)}`,
    f.to && `received before ${dateTimeText(f.to)}`,
    f.severity && `severity ${f.severity}`,
    f.loginId !== undefined && `person #${f.loginId}`,
    f.clientApp && `client app ${f.clientApp}`,
    f.q && `search "${f.q}"`
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : 'no filters (everything)'
}
